import React, { useState, useEffect } from "react";
import useApiFetcher from "../../hooks/useApiFetcher";

import { MdDelete } from "react-icons/md";
import LoaderSpiner from "../LoaderSpiner";
import { GoPencil } from "react-icons/go";
import { useTheme } from "../BrandColors/Context/ThemeContext";

const CustomerManagement = () => {

    const dealershipTheme = useTheme() || "#3b82f6";

    const [customers, setCustomers] = useState([]);
    const [formData, setFormData] = useState({
        customerName: "",
        phoneNumber: "",
        email: "",
        address: "",
    });
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const { apiFetcher } = useApiFetcher();
    const [isLoading, setIsLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [customersPerPage] = useState(5);

    // Pagination Logic
    const indexOfLastCustomer = currentPage * customersPerPage;
    const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
    const currentCustomers = customers.slice(indexOfFirstCustomer, indexOfLastCustomer);

    const totalPages = Math.ceil(customers.length / customersPerPage) || 1;

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleEditCancel = () => {
        setFormData({
            customerName: "",
            phoneNumber: "",
            email: "",
            address: "",
        })
        setEditMode(false)
    }

    const user = JSON.parse(localStorage.getItem("user"));

    // Fetch all customers
    const fetchCustomers = async () => {
        try {
            const response = await apiFetcher(`/customers?dealershipId=${user?.dealershipId?._id}`);
            setCustomers(response);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching customers:", error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    // Handle form changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle form submission (Create or Update)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editMode) {
            try {
                const response = await apiFetcher(`/customers/${editId}`, "PUT", formData);
                setCustomers((prev) =>
                    prev.map((customer) =>
                        customer._id === editId ? response : customer
                    )
                );
                setEditMode(false);
                setEditId(null);
                setFormData({
                    customerName: "",
                    phoneNumber: "",
                    email: "",
                    address: "",
                });
            } catch (error) {
                console.error("Error updating customer:", error);
            }
        } else {
            try {
                const response = await apiFetcher("/customers", "POST", { ...formData, dealershipId: user?.dealershipId?._id });
                setCustomers((prev) => [...prev, response]);
                setFormData({
                    customerName: "",
                    phoneNumber: "",
                    email: "",
                    address: "",
                });
            } catch (error) {
                console.error("Error creating customer:", error);
            }
        }
    };

    // Handle edit button click
    const handleEdit = (customer) => {
        setEditMode(true);
        setEditId(customer._id);
        setFormData({
            customerName: customer.customerName,
            phoneNumber: customer.phoneNumber,
            email: customer.email,
            address: customer.address,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            await apiFetcher(`/customers/${id}`, "DELETE");
            setCustomers((prev) => prev.filter((customer) => customer._id !== id));
        } catch (error) {
            console.error("Error deleting customer:", error);
        }
    };

    return (
        <div className="glass-panel w-full flex flex-col p-6 animate-fade-in-up mt-8  mx-auto">

            {/* Form Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h2 className="text-2xl font-bold">Customer Management</h2>
            </div>

            <form onSubmit={handleSubmit} className="bg-white/50 border border-blue-200/50 rounded-xl p-6 mb-8 shadow-lg">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b border-blue-200/50 pb-2">
                    {editMode ? "Edit Customer Details" : "Add New Customer"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                        { label: "Customer Name", name: "customerName", type: "text" },
                        { label: "Phone Number", name: "phoneNumber", type: "text" },
                        { label: "Email", name: "email", type: "email" },
                        { label: "Address", name: "address", type: "text" },
                    ].map(({ label, name, type }) => (
                        <div className="flex flex-col gap-1.5" key={name}>
                            <label className="text-sm font-medium text-slate-700" htmlFor={name}>{label}</label>
                            <input
                                id={name}
                                type={type}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                className="input-field"
                                required={name !== "address"}
                                placeholder={`Enter ${label.toLowerCase()}`}
                            />
                        </div>
                    ))}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-blue-200/50">
                    {editMode && (
                        <button
                            type="button"
                            className="px-5 py-2.5 rounded-lg font-medium text-slate-700 bg-white hover:bg-blue-100 transition-colors border border-blue-200"
                            onClick={handleEditCancel}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="submit"
                        className="px-5 py-2.5 rounded-lg font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
                        style={{ backgroundColor: dealershipTheme }}
                    >
                        {editMode ? "Update Customer" : "Add Customer"}
                    </button>
                </div>
            </form>

            {/* Customer List */}
            {isLoading ? (
                <div className="flex justify-center items-center py-20 flex-1">
                    <LoaderSpiner />
                </div>
            ) : customers.length === 0 ? (
                <div className="flex justify-center items-center py-16 bg-white/30 rounded-lg border border-blue-200/50">
                    <p className="text-slate-600">No customers found.</p>
                </div>
            ) : (
                <div className="flex-1 flex flex-col">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Customer Directory</h3>

                    <div className="w-full overflow-x-auto rounded-lg border border-blue-200/50 scrollbar-hide">
                        <table className="w-full text-left border-collapse min-w-max">
                            <thead>
                                <tr className="text-white text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: dealershipTheme }}>
                                    <th className="px-4 py-4 rounded-tl-lg">Customer Name</th>
                                    <th className="px-4 py-4">Phone Number</th>
                                    <th className="px-4 py-4">Email</th>
                                    <th className="px-4 py-4">Address</th>
                                    <th className="px-4 py-4 rounded-tr-lg">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50 bg-white/30">
                                {currentCustomers.map((customer) => (
                                    <tr key={customer._id} className="text-slate-700 text-sm hover:bg-white/5 transition-colors group">
                                        <td className="px-4 py-4 font-medium">{customer.customerName}</td>
                                        <td className="px-4 py-4">{customer.phoneNumber}</td>
                                        <td className="px-4 py-4">{customer.email}</td>
                                        <td className="px-4 py-4">{customer.address || "N/A"}</td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-3">
                                                {/* Edit Button */}
                                                <button
                                                    onClick={() => handleEdit(customer)}
                                                    className="text-primary-400 hover:text-primary-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-primary-900/30"
                                                    title="Edit"
                                                >
                                                    <GoPencil size={16} />
                                                </button>

                                                {/* Delete Button */}
                                                <button
                                                    onClick={() => handleDelete(customer._id)}
                                                    className="text-red-400 hover:text-red-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-red-900/30"
                                                    title="Delete"
                                                >
                                                    <MdDelete size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center mt-6 gap-2 pt-4">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => paginate(currentPage - 1)}
                                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-slate-700 hover:bg-blue-100"
                            >
                                Prev
                            </button>

                            <div className="flex gap-1">
                                {[...Array(totalPages).keys()].map((page) => (
                                    <button
                                        key={page + 1}
                                        onClick={() => paginate(page + 1)}
                                        className={`w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm transition-all ${currentPage === page + 1
                                            ? 'text-white shadow-lg scale-105'
                                            : 'bg-white text-slate-600 hover:bg-blue-100 hover:text-white'
                                            }`}
                                        style={currentPage === page + 1 ? { backgroundColor: dealershipTheme } : {}}
                                    >
                                        {page + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => paginate(currentPage + 1)}
                                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-slate-700 hover:bg-blue-100"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomerManagement;
